import { useClinicalRecordLock } from '../../../../components/clinical-record-lock';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton, TabContext, TabPanel } from '@mui/lab';
import {
  RHFSwitch,
  RHFTextField,
  RHFSelect,
} from '../../../../components/hook-form';
import FormProvider from '../../../../components/hook-form/FormProvider';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from '../../../../components/snackbar';
import { GlaucomaReferralRefinementService } from '../../../../api/clinical-pathways/grr.service';
import ImagingUploadSection from '../../../../components/image-upload/ImagingUploadSection';
import { UploadService } from '../../../../api/upload.service';
import { useAppContext } from '../../../../providers/AppContext';

const GLAUCOMA_RISK_FACTORS = [
  'Family history of glaucoma',
  'African-Caribbean heritage',
  'Age 40+',
  'Age 60+',
  'Diabetes',
  'High myopia',
  'Steroid use',
  'Previous eye trauma',
  'Hypertension',
  'Migraine',
  'Vasospasm',
  'Other (specify)',
];

const IOP_METHODS = [
  'Goldmann Applanation',
  'Non-contact tonometer (NCT)',
  'iCare rebound',
  'Pascal DCT',
  'Other (specify)',
];

const VISUAL_FIELD_TESTS = [
  'Humphrey 24-2',
  'Humphrey 30-2',
  'Humphrey 10-2',
  'Frequency Doubling Technology (FDT)',
  'Octopus',
  'Other (specify)',
];

const OCT_CLASSIFICATIONS = [
  'Normal',
  'Borderline',
  'Outside Normal Limits',
  'Artifact',
  'Not Performed',
];

const GONIOSCOPY_FINDINGS = [
  'Open angles',
  'Narrow angles',
  'Closed angles',
  'Pigment dispersion',
  'PAS (Peripheral Anterior Synechiae)',
  'Neovascularization',
  'Other (specify)',
];

const OUTCOME_OPTIONS = [
  'Refer to Hospital Eye Service (HES)',
  'Monitor in community',
  'Discharge to routine care',
];

const RECALL_INTERVALS = [
  '1 month',
  '3 months',
  '6 months',
  '12 months',
  '24 months',
  'Discharge',
];

type Props = {
  clinicalRecordId?: number;
  grrRecord?: any;
  onUpdate?: () => void;
};

type FormValuesProps = {
  riskFactors: string[];
  riskFactorsOther: string;
  iopMethod: string;
  iopMethodOther: string;
  iopNotes: string;
  visualFieldTest: string;
  rightEyeMD: any;
  leftEyeMD: any;
  rightEyePSD: any;
  leftEyePSD: any;
  visualFieldReliability: string;
  visualFieldResult: string;
  visualFieldNotes: string;
  rightCDRatio: any;
  leftCDRatio: any;
  rightDiscDescription: string;
  leftDiscDescription: string;
  discAsymmetry: string;
  rightOctClassification: string;
  leftOctClassification: string;
  rightRnflThickness: any;
  leftRnflThickness: any;
  rightGccThickness: any;
  leftGccThickness: any;
  octNotes: string;
  rightGonioscopy: string;
  leftGonioscopy: string;
  rightGonioscopyNotes: string;
  leftGonioscopyNotes: string;
  gonioscopyTechnique: string;
  clinicalImpression: string;
  clinicalNotes: string;
  outcome: string;
  recallInterval: string;
  referralUrgency: string;
  managementPlan: string;
  patientUnderstood: boolean;
  visualFieldImages?: string[];
  opticDiscImagesRight?: string[];
  opticDiscImagesLeft?: string[];
  octImagesRight?: string[];
  octImagesLeft?: string[];
  octReportImages?: string[];
};

function GlaucomaReferralRefinementForm({
  clinicalRecordId,
  grrRecord,
  onUpdate,
}: Props) {
  const lock = useClinicalRecordLock();
  const sections = [
    { id: 'riskFactors', label: 'Risk Factors' },
    { id: 'iop', label: 'IOP Measurements' },
    { id: 'visualFields', label: 'Visual Fields' },
    { id: 'imaging', label: 'Imaging' },
    { id: 'outcome', label: 'Outcome' },
  ] as const;

  type SectionId = (typeof sections)[number]['id'];

  const [currentSection, setCurrentSection] = useState<SectionId>(
    sections[0].id,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Image Previews State
  const [visualFieldPreviews, setVisualFieldPreviews] = useState<string[]>([]);
  const [opticDiscRightPreviews, setOpticDiscRightPreviews] = useState<
    string[]
  >([]);
  const [opticDiscLeftPreviews, setOpticDiscLeftPreviews] = useState<string[]>(
    [],
  );
  const [octRightPreviews, setOctRightPreviews] = useState<string[]>([]);
  const [octLeftPreviews, setOctLeftPreviews] = useState<string[]>([]);
  const [octReportPreviews, setOctReportPreviews] = useState<string[]>([]);

  const nextSection = () => {
    const currentIndex = sections.findIndex((s) => s.id === currentSection);
    if (currentIndex < sections.length - 1) {
      setCurrentSection(sections[currentIndex + 1].id);
      window.scrollTo(0, 0);
    }
  };

  const prevSection = () => {
    const currentIndex = sections.findIndex((s) => s.id === currentSection);
    if (currentIndex > 0) {
      setCurrentSection(sections[currentIndex - 1].id);
      window.scrollTo(0, 0);
    }
  };

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: SectionId,
  ) => {
    setCurrentSection(newValue);
  };
  interface IOPReadings {
    readings: number[];
    average: number;
  }

  const [iopReadings, setIopReadings] = useState<{
    right: IOPReadings;
    left: IOPReadings;
  }>({
    right: { readings: [0, 0, 0], average: 0 },
    left: { readings: [0, 0, 0], average: 0 },
  });

  const defaultValues: FormValuesProps = {
    riskFactors: [],
    riskFactorsOther: '',
    iopMethod: '',
    iopMethodOther: '',
    iopNotes: '',
    visualFieldTest: '',
    rightEyeMD: '',
    leftEyeMD: '',
    rightEyePSD: '',
    leftEyePSD: '',
    visualFieldReliability: '',
    visualFieldResult: '',
    visualFieldNotes: '',
    rightCDRatio: '',
    leftCDRatio: '',
    rightDiscDescription: '',
    leftDiscDescription: '',
    discAsymmetry: '',
    rightOctClassification: '',
    leftOctClassification: '',
    rightRnflThickness: '',
    leftRnflThickness: '',
    rightGccThickness: '',
    leftGccThickness: '',
    octNotes: '',
    rightGonioscopy: '',
    leftGonioscopy: '',
    rightGonioscopyNotes: '',
    leftGonioscopyNotes: '',
    gonioscopyTechnique: '',
    clinicalImpression: '',
    clinicalNotes: '',
    outcome: '',
    recallInterval: '',
    referralUrgency: '',
    managementPlan: '',
    patientUnderstood: false,
  };

  const methods = useForm<FormValuesProps>({ defaultValues });
  const { handleSubmit, watch, setValue, reset } = methods;

  const { patientId } = useParams();
  const { getAllClinicalRecords } = useAppContext();

  const watchIopMethod = watch('iopMethod');
  const watchOutcome = watch('outcome');
  const watchRiskFactors = watch('riskFactors', []);

  const handleIopReading = (eye: 'rightEye' | 'leftEye', value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    const handleIOPReadingChange = (
      eye: 'right' | 'left',
      readingIndex: number,
      value: number,
    ) => {
      const updatedReadings = { ...iopReadings };

      // Update the specific reading
      updatedReadings[eye].readings[readingIndex] = value;

      // Calculate average if we have 3 readings
      if (updatedReadings[eye].readings.length === 3) {
        const sum = updatedReadings[eye].readings.reduce(
          (a: number, b: number) => a + b,
          0,
        );
        updatedReadings[eye].average = parseFloat((sum / 3).toFixed(1));
      }

      setIopReadings(updatedReadings);
    };

    const eyeIndex = eye === 'rightEye' ? 'right' : 'left';
    const readingIndex = iopReadings[eyeIndex].readings.length;
    handleIOPReadingChange(eyeIndex, readingIndex, numValue);
  };

  const handleImageUpload = async (file: File) => {
    try {
      const uploadService = new UploadService();
      const response = await uploadService.uploadFile(file);
      return response.data.url;
    } catch (error) {
      console.error('Upload failed', error);
      enqueueSnackbar('Image upload failed', { variant: 'error' });
      throw error;
    }
  };

  const onSubmit = async (data: FormValuesProps) => {
    try {
      setIsSubmitting(true);
      const service = new GlaucomaReferralRefinementService();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
        visualFieldImages,
        opticDiscImagesRight,
        opticDiscImagesLeft,
        octImagesRight,
        octImagesLeft,
        octReportImages,
        id,
        createdDate,
        updatedDate,
        ...rest
      } = data as any;

      const payload = {
        ...rest,
        visualFieldImages: visualFieldPreviews,
        opticDiscImagesRight: opticDiscRightPreviews,
        opticDiscImagesLeft: opticDiscLeftPreviews,
        octImagesRight: octRightPreviews,
        octImagesLeft: octLeftPreviews,
        octReportImages: octReportPreviews,
        iopReadings: {
          rightEye: {
            readings: iopReadings.right.readings,
            average: iopReadings.right.average,
          },
          leftEye: {
            readings: iopReadings.left.readings,
            average: iopReadings.left.average,
          },
        },
        clinicalRecordId,
      };

      // Amending a signed record: stage the change instead of saving it.
      if (lock.stageIfAmending('glaucomaReferralRefinement', payload)) return;

      if (grrRecord?.id) {
        await service.update(patientId!, grrRecord.id, payload);
        enqueueSnackbar('GRR updated successfully', { variant: 'success' });
      } else {
        await service.create(patientId!, payload);
        enqueueSnackbar('GRR saved successfully', { variant: 'success' });
      }
      if (onUpdate) {
        await onUpdate();
      } else {
        getAllClinicalRecords(patientId);
      }
    } catch (error) {
      enqueueSnackbar('Failed to save GRR', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (grrRecord && grrRecord.id) {
      const flat = {
        ...(grrRecord.iop || {}),
        ...(grrRecord.visualFields || {}),
        ...(grrRecord.opticDisc || {}),
        ...(grrRecord.octImaging || {}),
        ...(grrRecord.gonioscopy || {}),
        ...(grrRecord.clinicalOutcome || {}),
      } as Partial<FormValuesProps> & { iopReadings?: any };

      const normalized: FormValuesProps = {
        ...defaultValues,
        ...flat,
        riskFactors: Array.isArray(grrRecord.riskFactors as any)
          ? (grrRecord.riskFactors as any)
          : [],
        patientUnderstood: !!flat.patientUnderstood,
      };
      reset(normalized);

      // Load images
      setVisualFieldPreviews(grrRecord.visualFields?.visualFieldImages || []);
      setOpticDiscRightPreviews(
        grrRecord.opticDisc?.opticDiscImagesRight || [],
      );
      setOpticDiscLeftPreviews(grrRecord.opticDisc?.opticDiscImagesLeft || []);
      setOctRightPreviews(grrRecord.octImaging?.octImagesRight || []);
      setOctLeftPreviews(grrRecord.octImaging?.octImagesLeft || []);
      setOctReportPreviews(grrRecord.octImaging?.octReportImages || []);

      const iopR = grrRecord?.iop?.iopReadings;
      if (iopR?.rightEye && iopR?.leftEye) {
        setIopReadings({
          right: {
            readings: iopR.rightEye.readings || [0, 0, 0],
            average: iopR.rightEye.average || 0,
          },
          left: {
            readings: iopR.leftEye.readings || [0, 0, 0],
            average: iopR.leftEye.average || 0,
          },
        });
      }
    }
  }, [grrRecord, reset]);

  const resetCurrentSection = () => {
    switch (currentSection) {
      case 'riskFactors':
        setValue('riskFactors', []);
        setValue('riskFactorsOther', '');
        break;
      case 'iop':
        setValue('iopMethod', '');
        setValue('iopMethodOther', '');
        setValue('iopNotes', '');
        setIopReadings({
          right: { readings: [0, 0, 0], average: 0 },
          left: { readings: [0, 0, 0], average: 0 },
        });
        break;
      case 'visualFields':
        setValue('visualFieldTest', '');
        setValue('rightEyeMD', '');
        setValue('leftEyeMD', '');
        setValue('rightEyePSD', '');
        setValue('leftEyePSD', '');
        setValue('visualFieldReliability', '');
        setValue('visualFieldResult', '');
        setValue('visualFieldNotes', '');
        break;
      case 'imaging':
        setValue('rightCDRatio', '');
        setValue('leftCDRatio', '');
        setValue('rightDiscDescription', '');
        setValue('leftDiscDescription', '');
        setValue('discAsymmetry', '');
        setValue('rightOctClassification', '');
        setValue('leftOctClassification', '');
        setValue('rightRnflThickness', '');
        setValue('leftRnflThickness', '');
        setValue('rightGccThickness', '');
        setValue('leftGccThickness', '');
        setValue('octNotes', '');
        setValue('rightGonioscopy', '');
        setValue('leftGonioscopy', '');
        setValue('rightGonioscopyNotes', '');
        setValue('leftGonioscopyNotes', '');
        setValue('gonioscopyTechnique', '');
        break;
      case 'outcome':
        setValue('clinicalImpression', '');
        setValue('clinicalNotes', '');
        setValue('outcome', '');
        setValue('recallInterval', '');
        setValue('referralUrgency', '');
        setValue('managementPlan', '');
        setValue('patientUnderstood', false);
        break;
      default:
        break;
    }
  };

  const renderRiskFactors = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Risk Factors
        </Typography>
        <Grid container spacing={2}>
          {GLAUCOMA_RISK_FACTORS.map((factor) => (
            <Grid item xs={12} sm={6} md={4} key={factor}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={watchRiskFactors?.includes(factor) || false}
                    onChange={(e) => {
                      const newRiskFactors = e.target.checked
                        ? [...(watchRiskFactors || []), factor]
                        : (watchRiskFactors || []).filter(
                            (f: string) => f !== factor,
                          );
                      setValue('riskFactors', newRiskFactors);
                    }}
                  />
                }
                label={factor}
              />
            </Grid>
          ))}
        </Grid>
        <RHFTextField
          name="riskFactorsOther"
          label="Other risk factors (specify)"
          fullWidth
          margin="normal"
          disabled={!watchRiskFactors?.includes('Other (specify)')}
        />
      </CardContent>
    </Card>
  );

  const renderIopSection = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Intraocular Pressure (IOP) Measurement
        </Typography>
        <RHFSelect
          name="iopMethod"
          label="Measurement Method"
          fullWidth
          margin="normal"
        >
          {IOP_METHODS.map((method) => (
            <MenuItem key={method} value={method}>
              {method}
            </MenuItem>
          ))}
        </RHFSelect>

        {watchIopMethod === 'Other (specify)' && (
          <RHFTextField
            name="iopMethodOther"
            label="Please specify method"
            fullWidth
            margin="normal"
          />
        )}

        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Right Eye
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map((readingNum) => (
            <Grid item xs={4} key={`right-${readingNum}`}>
              <TextField
                label={`Reading ${readingNum} (mmHg)`}
                type="number"
                fullWidth
                onChange={(e) => {
                  const value = e.target.value;
                  handleIopReading('rightEye', value);
                }}
                InputProps={{
                  inputProps: { min: 0, max: 60, step: 0.1 },
                }}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Typography variant="body2">
              Average:{' '}
              {iopReadings.right.average > 0
                ? `${iopReadings.right.average} mmHg`
                : 'N/A'}
              {iopReadings.right.average > 21 && ' (Elevated)'}
            </Typography>
          </Grid>
        </Grid>

        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
          Left Eye
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map((readingNum) => (
            <Grid item xs={4} key={`left-${readingNum}`}>
              <TextField
                label={`Reading ${readingNum} (mmHg)`}
                type="number"
                fullWidth
                onChange={(e) => {
                  const value = e.target.value;
                  handleIopReading('leftEye', value);
                }}
                InputProps={{
                  inputProps: { min: 0, max: 60, step: 0.1 },
                }}
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Typography variant="body2">
              Average:{' '}
              {iopReadings.left.average > 0
                ? `${iopReadings.left.average} mmHg`
                : 'N/A'}
              {iopReadings.left.average > 21 && ' (Elevated)'}
            </Typography>
          </Grid>
        </Grid>

        <RHFTextField
          name="iopNotes"
          label="Additional Notes"
          fullWidth
          multiline
          rows={2}
          margin="normal"
        />
      </CardContent>
    </Card>
  );

  const renderVisualFields = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Visual Fields
        </Typography>
        <RHFSelect
          name="visualFieldTest"
          label="Type of Test"
          fullWidth
          margin="normal"
        >
          {VISUAL_FIELD_TESTS.map((test) => (
            <MenuItem key={test} value={test}>
              {test}
            </MenuItem>
          ))}
        </RHFSelect>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="rightEyeMD"
              label="Right Eye MD (dB)"
              type="number"
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="leftEyeMD"
              label="Left Eye MD (dB)"
              type="number"
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="rightEyePSD"
              label="Right Eye PSD (dB)"
              type="number"
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <RHFTextField
              name="leftEyePSD"
              label="Left Eye PSD (dB)"
              type="number"
              fullWidth
              margin="normal"
            />
          </Grid>
        </Grid>

        <RHFSelect
          name="visualFieldReliability"
          label="Reliability"
          fullWidth
          margin="normal"
        >
          <MenuItem value="Reliable">Reliable</MenuItem>
          <MenuItem value="Questionable">Questionable</MenuItem>
          <MenuItem value="Unreliable">Unreliable</MenuItem>
        </RHFSelect>

        <RHFSelect
          name="visualFieldResult"
          label="Result Classification"
          fullWidth
          margin="normal"
        >
          <MenuItem value="Normal">Normal</MenuItem>
          <MenuItem value="Borderline">Borderline</MenuItem>
          <MenuItem value="Outside Normal Limits">
            Outside Normal Limits
          </MenuItem>
          <MenuItem value="Abnormal">Abnormal</MenuItem>
        </RHFSelect>

        <RHFTextField
          name="visualFieldNotes"
          label="Additional Notes"
          fullWidth
          multiline
          rows={2}
          margin="normal"
        />

        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Please attach visual field printout if available
        </Typography>
        <ImagingUploadSection
          title="Visual Field Printouts"
          previews={visualFieldPreviews}
          setPreviews={setVisualFieldPreviews}
          maxImages={4}
          onUpload={handleImageUpload}
        />
      </CardContent>
    </Card>
  );

  const renderOpticDiscAssessment = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Optic Disc Assessment
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Right Eye
            </Typography>
            <RHFTextField
              name="rightCDRatio"
              label="Cup-to-Disc Ratio (0.0-1.0)"
              type="number"
              inputProps={{ min: 0, max: 1, step: 0.05 }}
              fullWidth
              margin="normal"
            />
            <RHFTextField
              name="rightDiscDescription"
              label="Disc Description"
              fullWidth
              multiline
              rows={2}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Left Eye
            </Typography>
            <RHFTextField
              name="leftCDRatio"
              label="Cup-to-Disc Ratio (0.0-1.0)"
              type="number"
              inputProps={{ min: 0, max: 1, step: 0.05 }}
              fullWidth
              margin="normal"
            />
            <RHFTextField
              name="leftDiscDescription"
              label="Disc Description"
              fullWidth
              multiline
              rows={2}
              margin="normal"
            />
          </Grid>
        </Grid>

        <RHFTextField
          name="discAsymmetry"
          label="Asymmetry Notes"
          fullWidth
          multiline
          rows={2}
          margin="normal"
        />

        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          Please attach fundus photos if available
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ImagingUploadSection
              title="Right Eye Fundus"
              previews={opticDiscRightPreviews}
              setPreviews={setOpticDiscRightPreviews}
              maxImages={4}
              onUpload={handleImageUpload}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ImagingUploadSection
              title="Left Eye Fundus"
              previews={opticDiscLeftPreviews}
              setPreviews={setOpticDiscLeftPreviews}
              maxImages={4}
              onUpload={handleImageUpload}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderOctImaging = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          OCT Imaging
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Right Eye
            </Typography>
            <RHFSelect
              name="rightOctClassification"
              label="RNFL Classification"
              fullWidth
              margin="normal"
            >
              {OCT_CLASSIFICATIONS.map((classification) => (
                <MenuItem
                  key={`right-${classification}`}
                  value={classification}
                >
                  {classification}
                </MenuItem>
              ))}
            </RHFSelect>
            <RHFTextField
              name="rightRnflThickness"
              label="RNFL Thickness (µm)"
              type="number"
              fullWidth
              margin="normal"
            />
            <RHFTextField
              name="rightGccThickness"
              label="GCC Thickness (µm)"
              type="number"
              fullWidth
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Left Eye
            </Typography>
            <RHFSelect
              name="leftOctClassification"
              label="RNFL Classification"
              fullWidth
              margin="normal"
            >
              {OCT_CLASSIFICATIONS.map((classification) => (
                <MenuItem key={`left-${classification}`} value={classification}>
                  {classification}
                </MenuItem>
              ))}
            </RHFSelect>
            <RHFTextField
              name="leftRnflThickness"
              label="RNFL Thickness (µm)"
              type="number"
              fullWidth
              margin="normal"
            />
            <RHFTextField
              name="leftGccThickness"
              label="GCC Thickness (µm)"
              type="number"
              fullWidth
              margin="normal"
            />
          </Grid>
        </Grid>

        <RHFTextField
          name="octNotes"
          label="OCT Notes"
          fullWidth
          multiline
          rows={2}
          margin="normal"
        />

        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          Please attach OCT scans if available
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ImagingUploadSection
              title="Right Eye OCT"
              previews={octRightPreviews}
              setPreviews={setOctRightPreviews}
              maxImages={4}
              onUpload={handleImageUpload}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ImagingUploadSection
              title="Left Eye OCT"
              previews={octLeftPreviews}
              setPreviews={setOctLeftPreviews}
              maxImages={4}
              onUpload={handleImageUpload}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <ImagingUploadSection
            title="OCT Report"
            previews={octReportPreviews}
            setPreviews={setOctReportPreviews}
            maxImages={4}
            onUpload={handleImageUpload}
          />
        </Box>
      </CardContent>
    </Card>
  );

  const renderGonioscopy = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Gonioscopy
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Right Eye
            </Typography>
            <RHFSelect
              name="rightGonioscopy"
              label="Angle Status"
              fullWidth
              margin="normal"
            >
              {GONIOSCOPY_FINDINGS.map((finding) => (
                <MenuItem key={`right-${finding}`} value={finding}>
                  {finding}
                </MenuItem>
              ))}
            </RHFSelect>
            <RHFTextField
              name="rightGonioscopyNotes"
              label="Additional Notes"
              fullWidth
              multiline
              rows={2}
              margin="normal"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Left Eye
            </Typography>
            <RHFSelect
              name="leftGonioscopy"
              label="Angle Status"
              fullWidth
              margin="normal"
            >
              {GONIOSCOPY_FINDINGS.map((finding) => (
                <MenuItem key={`left-${finding}`} value={finding}>
                  {finding}
                </MenuItem>
              ))}
            </RHFSelect>
            <RHFTextField
              name="leftGonioscopyNotes"
              label="Additional Notes"
              fullWidth
              multiline
              rows={2}
              margin="normal"
            />
          </Grid>
        </Grid>

        <RHFTextField
          name="gonioscopyTechnique"
          label="Technique Used"
          fullWidth
          margin="normal"
        />
      </CardContent>
    </Card>
  );

  const renderClinicalImpression = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Clinical Impression & Outcome
        </Typography>

        <RHFSelect
          name="clinicalImpression"
          label="Clinical Impression"
          fullWidth
          margin="normal"
        >
          <MenuItem value="Normal">Normal</MenuItem>
          <MenuItem value="Ocular Hypertension (OHT)">
            Ocular Hypertension (OHT)
          </MenuItem>
          <MenuItem value="Glaucoma Suspect">Glaucoma Suspect</MenuItem>
          <MenuItem value="Glaucoma Confirmed">Glaucoma Confirmed</MenuItem>
          <MenuItem value="Other">Other (please specify)</MenuItem>
        </RHFSelect>

        <RHFTextField
          name="clinicalNotes"
          label="Clinical Notes"
          fullWidth
          multiline
          rows={3}
          margin="normal"
        />

        <RHFSelect
          name="outcome"
          label="Outcome"
          fullWidth
          margin="normal"
          required
        >
          {OUTCOME_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </RHFSelect>

        {watchOutcome === 'Monitor in community' && (
          <RHFSelect
            name="recallInterval"
            label="Recall Interval"
            fullWidth
            margin="normal"
            required
          >
            {RECALL_INTERVALS.map((interval) => (
              <MenuItem key={interval} value={interval}>
                {interval}
              </MenuItem>
            ))}
          </RHFSelect>
        )}

        {watchOutcome === 'Refer to Hospital Eye Service (HES)' && (
          <RHFTextField
            name="referralUrgency"
            label="Referral Urgency"
            fullWidth
            margin="normal"
            select
            SelectProps={{ native: true }}
          >
            <option value="">Select urgency</option>
            <option value="Emergency (same day)">Emergency (same day)</option>
            <option value="Urgent (within 1 week)">
              Urgent (within 1 week)
            </option>
            <option value="Soon (within 2 weeks)">Soon (within 2 weeks)</option>
            <option value="Routine (within 6-8 weeks)">
              Routine (within 6-8 weeks)
            </option>
          </RHFTextField>
        )}

        <RHFTextField
          name="managementPlan"
          label="Management Plan"
          fullWidth
          multiline
          rows={3}
          margin="normal"
        />

        <FormControlLabel
          control={
            <Checkbox name="patientUnderstood" color="primary" required />
          }
          label="I confirm that I have discussed the findings and management plan with the patient"
          sx={{ mt: 2, display: 'block' }}
        />
      </CardContent>
    </Card>
  );

  const renderSection = () => {
    switch (currentSection) {
      case 'riskFactors':
        return renderRiskFactors();
      case 'iop':
        return renderIopSection();
      case 'visualFields':
        return renderVisualFields();
      case 'imaging':
        return (
          <>
            {renderOpticDiscAssessment()}
            {renderOctImaging()}
            {renderGonioscopy()}
          </>
        );
      case 'outcome':
        return renderClinicalImpression();
      default:
        return null;
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h4" gutterBottom>
          Glaucoma Referral Refinement
        </Typography>
        <TabContext value={currentSection}>
          <Card>
            <Tabs
              value={currentSection}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ bgcolor: 'background.never', px: 2 }}
            >
              {sections.map((section) => (
                <Tab
                  key={section.id}
                  label={section.label}
                  value={section.id}
                />
              ))}
            </Tabs>
            <CardContent>
              <TabPanel value="riskFactors">{renderRiskFactors()}</TabPanel>
              <TabPanel value="iop">{renderIopSection()}</TabPanel>
              <TabPanel value="visualFields">{renderVisualFields()}</TabPanel>
              <TabPanel value="imaging">
                {renderOpticDiscAssessment()}
                {renderOctImaging()}
                {renderGonioscopy()}
              </TabPanel>
              <TabPanel value="outcome">{renderClinicalImpression()}</TabPanel>

              <Box
                sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}
              >
                <Button
                  variant="outlined"
                  onClick={prevSection}
                  disabled={sections[0].id === currentSection}
                >
                  Previous
                </Button>
                <Box sx={{ display: 'flex', gap: '10px' }}>
                  <Button
                    data-lock-hide
                    variant="outlined"
                    onClick={resetCurrentSection}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    onClick={nextSection}
                    disabled={
                      currentSection === sections[sections.length - 1].id
                    }
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </TabContext>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            sx={{ ml: 2 }}
          >
            Save Changes
          </LoadingButton>
        </Box>
      </Box>
    </FormProvider>
  );
}

export default GlaucomaReferralRefinementForm;
